import React, { useState, useEffect, useMemo } from "react";
import type {
  PropertyDefinition,
  MaterialType,
  SectionType,
} from "@/types/property";
import {
  getDesignationsForSectionType,
  getDesignationData,
} from "@/data/sectionDesignations";

interface SectionTabProps {
  property: Partial<PropertyDefinition>;
  onUpdate: (updates: Partial<PropertyDefinition>) => void;
  isEditing?: boolean; // True when editing existing property
}

/**
 * SectionTab Component
 *
 * Allows users to define section properties for beams
 * - Property ID (auto-generated, read-only)
 * - Material selection
 * - Section type and designation (for Steel)
 *
 * Note: Beam assignment is handled separately via Apply by Click, Manual Input, or Apply to Selected
 */
export const SectionTab: React.FC<SectionTabProps> = ({
  property,
  onUpdate,
  isEditing = false,
}) => {
  const [pId, setPId] = useState(property.P_ID || "");
  const [material, setMaterial] = useState<MaterialType>(
    property.material || "None"
  );
  const [sectionType, setSectionType] = useState<SectionType | "">(
    property.sectionType || ""
  );
  const [designation, setDesignation] = useState(
    property.sectionProperties?.designation || ""
  );

  // Custom Section state
  const [customIx, setCustomIx] = useState<number>(0);
  const [customIy, setCustomIy] = useState<number>(0);
  const [customIz, setCustomIz] = useState<number>(0);
  const [customAx, setCustomAx] = useState<number>(0);
  const [customAy, setCustomAy] = useState<number>(0);
  const [customAz, setCustomAz] = useState<number>(0);
  const [customPerimeter, setCustomPerimeter] = useState<number>(0);
  const [customWidth, setCustomWidth] = useState<number>(0);
  const [customDepth, setCustomDepth] = useState<number>(0);
  const [customOrientation, setCustomOrientation] = useState<
    "horizontal" | "vertical"
  >("horizontal");

  // Sync local state when property prop changes (e.g., when editing different P_ID)
  useEffect(() => {
    console.log("🔄 SectionTab - Syncing property data:", property);
    setPId(property.P_ID || "");
    setMaterial(property.material || "None");
    setSectionType(property.sectionType || "");
    setDesignation(property.sectionProperties?.designation || "");

    // Load custom section values if available
    if (
      property.sectionType === "Custom Section" &&
      property.sectionProperties
    ) {
      setCustomIx(property.sectionProperties.momentOfInertiaY || 0);
      setCustomIy(property.sectionProperties.momentOfInertiaZ || 0);
      setCustomIz(property.sectionProperties.torsionalConstant || 0);
      setCustomAx(property.sectionProperties.area || 0);
      setCustomAy(property.sectionProperties.shearAreaY || 0);
      setCustomAz(property.sectionProperties.shearAreaZ || 0);
      setCustomPerimeter(property.sectionProperties.perimeter || 0);
      setCustomWidth(property.sectionProperties.width || 0);
      setCustomDepth(property.sectionProperties.depth || 0);
    }

    console.log("✅ SectionTab - State updated:", {
      pId: property.P_ID,
      material: property.material,
      sectionType: property.sectionType,
      designation: property.sectionProperties?.designation,
    });
  }, [property]);

  // Get available designations for the selected section type
  const availableDesignations = useMemo(() => {
    if (!sectionType) return [];
    return getDesignationsForSectionType(sectionType);
  }, [sectionType]);

  // Get selected designation data for display
  const selectedDesignationData = useMemo(() => {
    if (!designation) return null;
    return getDesignationData(designation);
  }, [designation]);

  // Update parent when values change
  useEffect(() => {
    // Get full section data if designation is selected
    const sectionData = designation ? getDesignationData(designation) : null;

    // For custom section, build custom properties
    if (sectionType === "Custom Section") {
      onUpdate({
        P_ID: pId,
        beamIds: [],
        material,
        sectionType: "Custom Section",
        sectionProperties: {
          designation: "Custom Section",
          momentOfInertiaY: customIx,
          momentOfInertiaZ: customIy,
          torsionalConstant: customIz,
          area: customAx,
          shearAreaY: customAy,
          shearAreaZ: customAz,
          perimeter: customPerimeter,
          width: customWidth,
          depth: customDepth,
        },
      });
    } else {
      onUpdate({
        P_ID: pId,
        beamIds: [],
        material,
        sectionType: sectionType || undefined,
        sectionProperties:
          sectionData || (designation ? { designation } : undefined),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pId,
    material,
    sectionType,
    designation,
    customIx,
    customIy,
    customIz,
    customAx,
    customAy,
    customAz,
    customPerimeter,
    customWidth,
    customDepth,
  ]);

  return (
    <div className="space-y-4">
      {/* Edit Mode Summary - Show current property details */}
      {isEditing && property.sectionProperties?.designation && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Current Property Configuration
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-blue-700">P_ID:</span>
              <span className="ml-2 font-medium text-blue-900">{pId}</span>
            </div>
            <div>
              <span className="text-blue-700">Beams:</span>
              <span className="ml-2 font-medium text-blue-900">
                {property.beamIds?.join(", ") || "None assigned"}
              </span>
            </div>
            <div>
              <span className="text-blue-700">Material:</span>
              <span className="ml-2 font-medium text-blue-900">{material}</span>
            </div>
            <div>
              <span className="text-blue-700">Section Type:</span>
              <span className="ml-2 font-medium text-blue-900">
                {sectionType}
              </span>
            </div>
            <div className="col-span-2">
              <span className="text-blue-700">Designation:</span>
              <span className="ml-2 font-medium text-blue-900">
                {designation}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Property ID - Auto-generated, Read-only */}
      <div>
        <label className="hidden block text-sm font-medium text-gray-700 mb-2">
          Property ID <span className="text-red-500">*</span>
        </label>
        <input
          type="hidden"
          value={pId}
          disabled={true}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded 
                     bg-gray-100 cursor-not-allowed text-gray-900"
          placeholder="Auto-generated"
          readOnly
        />
        <p className="text-xs text-gray-500 mt-1">
          {isEditing ? "Property ID cannot be changed" : ""}
        </p>
      </div>

      {/* Material Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Material
        </label>
        <select
          value={material}
          onChange={(e) => {
            setMaterial(e.target.value as MaterialType);
            if (e.target.value === "None") {
              setSectionType("");
              setDesignation("");
            }
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
        >
          <option value="None">None</option>
          <option value="Steel">Steel</option>
        </select>
      </div>

      {/* Section Type - Only for Steel */}
      {material === "Steel" && (
        <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700">
            Steel Section Configuration
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Type
            </label>
            <select
              value={sectionType}
              onChange={(e) => {
                setSectionType(e.target.value as SectionType);
                setDesignation(""); // Reset designation when type changes
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            >
              <option value="">Select Section Type...</option>
              <option value="Bulb Angle">Bulb Angle</option>
              <option value="Channel">Channel (ISJC, ISMC, ISLC)</option>
              <option value="Custom Section">Custom Section</option>
              <option value="Equal Angle">Equal Angle (ISA)</option>
              <option value="I-Beam">I-Beam (ISJB, ISMB, ISWB, etc.)</option>
              <option value="Tee Bar">Tee Bar (IST)</option>
              <option value="Unequal Angle">Unequal Angle (ISA)</option>
            </select>
          </div>

          {/* Designation Dropdown - Only when section type is selected */}
          {sectionType && sectionType !== "Custom Section" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Designation
              </label>
              <select
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded 
                           focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="">Select Designation...</option>
                {availableDesignations.map((opt) => (
                  <option key={opt.designation} value={opt.designation}>
                    {opt.designation}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {availableDesignations.length} designations available for{" "}
                {sectionType}
              </p>
            </div>
          )}

          {/* Custom Section Inputs - Only when Custom Section is selected */}
          {sectionType === "Custom Section" && (
            <div className="space-y-3">
              <h5 className="text-sm font-semibold text-gray-800">
                Custom Section Properties
              </h5>

              {/* Moment of Inertia */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Ix (cm⁴) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customIx}
                    onChange={(e) =>
                      setCustomIx(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Iy (cm⁴) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customIy}
                    onChange={(e) =>
                      setCustomIy(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Iz (cm⁴) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customIz}
                    onChange={(e) =>
                      setCustomIz(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Areas */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Ax (cm²) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customAx}
                    onChange={(e) =>
                      setCustomAx(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Ay (cm²) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customAy}
                    onChange={(e) =>
                      setCustomAy(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Az (cm²) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={customAz}
                    onChange={(e) =>
                      setCustomAz(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Perimeter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Perimeter (cm) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={customPerimeter}
                  onChange={(e) =>
                    setCustomPerimeter(parseFloat(e.target.value) || 0)
                  }
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  step="0.01"
                />
              </div>

              {/* Width and Depth */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Width (cm)
                  </label>
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) =>
                      setCustomWidth(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0 (optional)"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Depth (cm)
                  </label>
                  <input
                    type="number"
                    value={customDepth}
                    onChange={(e) =>
                      setCustomDepth(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0 (optional)"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Rectangle Orientation - Only show if both width and depth are provided */}
              {customWidth > 0 && customDepth > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Rectangle Orientation
                  </label>
                  <select
                    value={customOrientation}
                    onChange={(e) =>
                      setCustomOrientation(
                        e.target.value as "horizontal" | "vertical"
                      )
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded 
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                  </select>
                </div>
              )}

              <p className="text-xs text-blue-600 mt-2">
                * Required fields. Width and Depth are optional but enable
                rectangle visualization.
              </p>
            </div>
          )}

          {/* Section Properties Display - Only when designation is selected */}
          {designation &&
            selectedDesignationData &&
            sectionType !== "Custom Section" && (
              <div className="mt-4 p-4 bg-white border border-gray-300 rounded">
                <h5 className="text-sm font-semibold text-gray-800 mb-3">
                  Section Properties
                </h5>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {/* Basic Dimensions */}
                  {"h" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Depth (h):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).h} mm
                      </span>
                    </div>
                  )}
                  {"b" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Width (b):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).b} mm
                      </span>
                    </div>
                  )}
                  {"tf" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Flange Thick. (tf):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).tf} mm
                      </span>
                    </div>
                  )}
                  {"tw" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Web Thick. (tw):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).tw} mm
                      </span>
                    </div>
                  )}

                  {/* Weight and Area */}
                  {"w" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Weight:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).w} kg/m
                      </span>
                    </div>
                  )}
                  {"a" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Area:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).a} cm²
                      </span>
                    </div>
                  )}

                  {/* Moment of Inertia */}
                  {"Ixx" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Ixx:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Ixx} cm⁴
                      </span>
                    </div>
                  )}
                  {"Iyy" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Iyy:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Iyy} cm⁴
                      </span>
                    </div>
                  )}

                  {/* Radius of Gyration */}
                  {"rxx" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">rxx:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).rxx} cm
                      </span>
                    </div>
                  )}
                  {"ryy" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">ryy:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).ryy} cm
                      </span>
                    </div>
                  )}

                  {/* Section Modulus */}
                  {"Zxx" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Zxx:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Zxx} cm³
                      </span>
                    </div>
                  )}
                  {"Zyy" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Zyy:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Zyy} cm³
                      </span>
                    </div>
                  )}

                  {/* Radii for angles and other sections */}
                  {"r1" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Root Radius (r1):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).r1} mm
                      </span>
                    </div>
                  )}
                  {"r2" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Toe Radius (r2):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).r2} mm
                      </span>
                    </div>
                  )}

                  {/* Channel specific */}
                  {"cyy" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Centroid (cyy):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).cyy} cm
                      </span>
                    </div>
                  )}

                  {/* Angle specific */}
                  {"A" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Leg A:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).A} mm
                      </span>
                    </div>
                  )}
                  {"B" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Leg B:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).B} mm
                      </span>
                    </div>
                  )}
                  {"t" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Thickness (t):</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).t} mm
                      </span>
                    </div>
                  )}
                  {"Cxx" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Cxx:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Cxx} cm
                      </span>
                    </div>
                  )}
                  {"Cyy" in selectedDesignationData && (
                    <div>
                      <span className="text-gray-600">Cyy:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(selectedDesignationData as any).Cyy} cm
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <svg
          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div>
          <p className="text-sm font-medium text-blue-900">
            Section Configuration
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Define section properties for this P_ID. These will apply to all
            beams using this property.
          </p>
        </div>
      </div>
    </div>
  );
};
